import { useEffect, useRef, useState } from 'react';
import { type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { useHeightObserver } from './useHeightObserver';
import { getPerformanceMark, usePerformanceAnalytics } from '../../hooks/usePerformanceAnalytics';

type ElementSizeChangeTimeline = Array<{ elapsed: number; height: number }>;

const TRACK_CHANGES_FOR_MS = 5000;

const getResizeTotal = (resizes: number[]) => {
	return resizes.reduce((total, current, index) => {
		if (index === 0) {
			return 0;
		}
		return total + Math.abs(current - resizes[index - 1]);
	}, 0);
};

// Collects resizes for TRACK_CHANGES_FOR_MS seconds after the first
// render of the consuming component.
const useEmitResizeAnalytics = ({
	isEnabled,
	appType,
	extensionType,
	forgeEnvironment,
	navigationTimeStart,
	resizedHeightValues,
}: {
	appType: 'UIKit' | 'CustomUI';
	extensionType: ForgeUIExtensionType['type'];
	forgeEnvironment: ForgeUIExtensionType['environmentType'];
	isEnabled: boolean;
	navigationTimeStart: number;
	resizedHeightValues: ElementSizeChangeTimeline;
}) => {
	const { captureEvent } = usePerformanceAnalytics({ isEnabled });
	const [isTrackingResize, setIsTrackingResize] = useState(isEnabled);

	useEffect(() => {
		if (!isTrackingResize) {
			return;
		}

		const timeoutId = setTimeout(() => {
			setIsTrackingResize(false);
			// Track events that have distinct size changes.
			if (resizedHeightValues.length > 1) {
				captureEvent({
					type: 'resized',
					eventData: {
						navigationTimeStart,
						resizedValues: resizedHeightValues,
						resizeTotal: getResizeTotal(resizedHeightValues.map(({ height }) => height)),
					},
				});
			}
		}, TRACK_CHANGES_FOR_MS);

		// Clear timeout if extension is removed before the analytic was sent
		return () => clearTimeout(timeoutId);
	}, [
		appType,
		extensionType,
		forgeEnvironment,
		isEnabled,
		isTrackingResize,
		resizedHeightValues,
		navigationTimeStart,
		captureEvent,
	]);

	return { isTrackingResize };
};

export const useResizeAnalytics = ({
	isEnabled,
	trackedAttributeValues: { appType, extensionType, forgeEnvironment },
}: {
	isEnabled: boolean;
	trackedAttributeValues: {
		appType: 'UIKit' | 'CustomUI';
		extensionType: ForgeUIExtensionType['type'];
		forgeEnvironment: ForgeUIExtensionType['environmentType'];
	};
}) => {
	const { containerRef, height } = useHeightObserver();
	const [elapsedBaselineMark] = useState(() => (isEnabled ? getPerformanceMark() : -1));
	const resizedHeightValues = useRef<ElementSizeChangeTimeline>([]);

	const { isTrackingResize } = useEmitResizeAnalytics({
		isEnabled,
		appType,
		extensionType,
		forgeEnvironment,
		// The first capture of performance.now() is when the consuming component
		// is mounted, the value is milliseconds since global window navigation
		// occurred (roughly).
		navigationTimeStart: elapsedBaselineMark,
		resizedHeightValues: resizedHeightValues.current,
	});

	useEffect(() => {
		if (height !== null && isTrackingResize && isEnabled) {
			resizedHeightValues.current.push({
				height,
				elapsed: getPerformanceMark() - elapsedBaselineMark,
			});
		}
	}, [height, isTrackingResize, isEnabled, elapsedBaselineMark]);

	return { containerRef };
};
